import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heart, Users, Shield, Calendar } from "lucide-react";

const About = () => {
  const { t } = useTranslation(['common']);

  return (
    <div className="min-h-screen bg-uplivion-lightBlue/30 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="mb-8">
            <Link to="/" className="text-uplivion-blue hover:underline mb-4 inline-block">
              ← Zurück zur Startseite
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Über uplivion</h1>
            <p className="text-xl text-gray-600">Ihre Lebensgeschichte verdient es, bewahrt zu werden.</p>
          </div>

          <div className="prose max-w-none">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
              <div className="flex items-center">
                <Calendar className="w-8 h-8 text-blue-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">Beta-Version</h3>
                  <p className="text-blue-800">
                    uplivion befindet sich derzeit in der Beta-Testphase. Wir arbeiten kontinuierlich 
                    daran, die beste Plattform für Ihre persönlichen Erinnerungen zu schaffen.
                  </p>
                </div>
              </div>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Unsere Mission</h2>
              <p className="mb-4 text-lg">
                uplivion wurde geschaffen, um Menschen dabei zu helfen, ihre Lebensgeschichte zu dokumentieren, 
                zu organisieren und mit den wichtigsten Menschen in ihrem Leben zu teilen. Wir glauben, 
                dass jede Lebensgeschichte einzigartig und wertvoll ist.
              </p>
              <p className="mb-4">
                In einer Zeit, in der Erinnerungen oft in verschiedenen Apps und Plattformen verstreut sind, 
                bietet uplivion einen zentralen Ort, um Ihr Leben chronologisch und bedeutungsvoll zu dokumentieren.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-6">Was macht uplivion besonders?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <Heart className="w-8 h-8 text-uplivion-orange flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Persönlich & Bedeutungsvoll</h3>
                    <p className="text-sm">
                      Organisieren Sie Ihre Erinnerungen nach Lebensphasen und geben Sie jedem 
                      Moment die Aufmerksamkeit, die er verdient.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <Users className="w-8 h-8 text-uplivion-blue flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Verbunden mit Freunden</h3>
                    <p className="text-sm">
                      Teilen Sie ausgewählte Erinnerungen mit Freunden und Familie, 
                      während Sie die Kontrolle über Ihre Privatsphäre behalten.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <Shield className="w-8 h-8 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Sicher & Privat</h3>
                    <p className="text-sm">
                      Ihre Daten gehören Ihnen. Wir verwenden modernste Sicherheitstechnologien 
                      und respektieren Ihre Privatsphäre.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <Calendar className="w-8 h-8 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Strukturiert & Organisiert</h3>
                    <p className="text-sm">
                      Von der Kindheit bis heute - organisieren Sie Ihr Leben in sinnvollen 
                      Abschnitten mit unserem innovativen Timeline-System.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Unser Ansatz</h2>
              <p className="mb-4">
                uplivion basiert auf der Überzeugung, dass Technologie menschliche Verbindungen 
                stärken sollte, nicht ersetzen. Deshalb konzentrieren wir uns auf:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Einfachheit:</strong> Intuitive Bedienung ohne komplizierte Features</li>
                <li><strong>Privatheit:</strong> Ihre Daten bleiben unter Ihrer Kontrolle</li>
                <li><strong>Bedeutung:</strong> Fokus auf wichtige Momente, nicht auf oberflächliche Inhalte</li>
                <li><strong>Langfristigkeit:</strong> Gebaut für die Bewahrung über Jahre hinweg</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Beta-Phase: Ihre Mithilfe ist gefragt</h2>
              <p className="mb-4">
                Als Beta-Nutzer sind Sie Teil der uplivion-Familie und helfen uns dabei, 
                die bestmögliche Plattform zu entwickeln. Ihre Rückmeldungen und Anregungen 
                fließen direkt in die Weiterentwicklung ein.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">Was bedeutet Beta für Sie?</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Kostenlose Nutzung aller Grundfunktionen</li>
                  <li>• Direkter Einfluss auf neue Features</li>
                  <li>• Regelmäßige Updates und Verbesserungen</li>
                  <li>• Persönlicher Support bei Fragen</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Kontakt & Support</h2>
              <p className="mb-4">
                Haben Sie Fragen, Anregungen oder benötigen Hilfe? Wir sind für Sie da:
              </p>
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Arc Rider Ventures GmbH</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Jänickendorfer Str. 64a<br/>
                    14943 Brandenburg<br/>
                    Deutschland
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    Amtsgericht Berlin Charlottenburg HRB199081B<br/>
                    Geschäftsführer: Jakob Jordan
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">Allgemeine Anfragen</h4>
                    <p className="text-sm text-gray-600">support@uplivion.life</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Technischer Support</h4>
                    <p className="text-sm text-gray-600">support@uplivion.life</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Feedback & Ideen</h4>
                    <p className="text-sm text-gray-600">support@uplivion.life</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Datenschutz</h4>
                    <p className="text-sm text-gray-600">support@uplivion.life</p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-8 border-t">
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild>
                <Link to="/">Zur Startseite</Link>
              </Button>
              <Button variant="outline" asChild>
                <a href="https://app.uplivion.life/register">Jetzt Beta testen</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
